import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { getCategories } from '../../../lib/wp-api';

const FilterSidebar = ({ isOpen, onClose, filters, onFilterChange, onClearFilters, products = [] }) => {
  const [priceRange, setPriceRange] = useState([0, 200]);
  const [searchTerm, setSearchTerm] = useState('');

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getCategories()
      .then((rows) => { if (!cancelled) setCategories(rows); })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, []);

  // Curated collections — these are WC categories under the hood (so the same
  // server-side fetch handles them), but we surface them separately from the
  // main Categories tree so they don't clutter that list. IDs match WC term IDs.
  const collections = [
    { id: 60, label: 'House of Lalay' },
    { id: 61, label: 'Lalay Essentials' },
    { id: 62, label: 'The Lalay Edit' },
  ];

  // Compute real counts from the current product set so users don't see filter
  // options that would silently produce zero results.
  const countAtLeast = (threshold) =>
    (products || []).filter((p) => (Number(p?.rating) || 0) >= threshold).length;
  const ratings = [
    { id: '4-plus', label: '4+ Stars', count: countAtLeast(4) },
    { id: '3-plus', label: '3+ Stars', count: countAtLeast(3) },
    { id: '2-plus', label: '2+ Stars', count: countAtLeast(2) },
    { id: '1-plus', label: '1+ Stars', count: countAtLeast(1) },
  ];

  const sizes = [
    { id: 'xs', label: 'XS', count: 0 },
    { id: 's', label: 'S', count: 0 },
    { id: 'm', label: 'M', count: 0 },
    { id: 'l', label: 'L', count: 0 },
    { id: 'xl', label: 'XL', count: 0 },
    { id: 'xxl', label: 'XXL', count: 0 }
  ];

  const handlePriceChange = (index, value) => {
    const newRange = [...priceRange];
    newRange[index] = parseInt(value);
    setPriceRange(newRange);
    onFilterChange('priceRange', newRange);
  };

  const handleCheckboxChange = (filterType, value, checked) => {
    const currentValues = filters?.[filterType] || [];
    let newValues;
    
    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues?.filter(v => v !== value);
    }
    
    onFilterChange(filterType, newValues);
  };

  // Calculate active filter count correctly
  const getActiveFilterCount = () => {
    let count = 0;
    
    // Count array filters (categories, brands, ratings, sizes)
    if (filters?.categories?.length > 0) count += filters?.categories?.length;
    if (filters?.brands?.length > 0) count += filters?.brands?.length;
    if (filters?.ratings?.length > 0) count += filters?.ratings?.length;
    if (filters?.sizes?.length > 0) count += filters?.sizes?.length;
    
    // Only count priceRange as 1 filter if it differs from default [0, 200]
    if (filters?.priceRange && 
        (filters?.priceRange?.[0] !== 0 || filters?.priceRange?.[1] !== 200)) {
      count += 1;
    }
    
    return count;
  };

  const FilterSection = ({ title, children, defaultOpen = true }) => {
    const [isExpanded, setIsExpanded] = useState(defaultOpen);
    
    return (
      <div className="border-b border-border pb-6 mb-6">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full mb-4 text-left"
        >
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          <Icon 
            name={isExpanded ? "ChevronUp" : "ChevronDown"} 
            size={16} 
            className="text-muted-foreground" 
          />
        </button>
        {isExpanded && children}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <div className={`
        fixed lg:static top-20 lg:top-0 left-0 z-40 h-[calc(100%-5rem)] w-80 bg-background border-r border-border
        transform transition-transform duration-300 lg:transform-none overflow-y-auto lg:h-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border lg:hidden">
          <h2 className="text-lg font-heading font-semibold text-foreground">Filters</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="px-6 py-2 lg:py-2">
          {/* Search */}
          <div className="mb-6">
            <Input
              type="search"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* Clear Filters */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-muted-foreground">
              {getActiveFilterCount()} {getActiveFilterCount() === 1 ? 'filter' : 'filters'} applied
            </span>
            <Button variant="ghost" size="sm" onClick={onClearFilters}>
              Clear All
            </Button>
          </div>

          {/* Price Range */}
          <FilterSection title="Price Range">
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={priceRange?.[0]}
                    onChange={(e) => handlePriceChange(0, e?.target?.value)}
                    className="text-center"
                  />
                </div>
                <span className="text-muted-foreground">to</span>
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder="Max"
                    value={priceRange?.[1]}
                    onChange={(e) => handlePriceChange(1, e?.target?.value)}
                    className="text-center"
                  />
                </div>
              </div>
              {/* Dual-range slider — two stacked range inputs. Z-index swaps based
                  on the min value: when min > 100, the user is reaching for the
                  high end of the LEFT thumb, so it goes on top; otherwise the max
                  thumb stays on top. Thumbs are colored differently in CSS. */}
              <div className="relative h-6 select-none">
                <div className="absolute top-1/2 -translate-y-1/2 w-full h-1.5 bg-muted rounded-full" />
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-1.5 bg-primary rounded-full"
                  style={{
                    left:  `${(priceRange?.[0] / 200) * 100}%`,
                    right: `${100 - (priceRange?.[1] / 200) * 100}%`,
                  }}
                />
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={priceRange?.[0]}
                  onChange={(e) => handlePriceChange(0, Math.min(parseInt(e?.target?.value), priceRange?.[1] - 1))}
                  className="oh-range-min absolute w-full h-6 appearance-none bg-transparent"
                  style={{ zIndex: priceRange?.[0] > 100 ? 5 : 3 }}
                  aria-label="Minimum price"
                />
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={priceRange?.[1]}
                  onChange={(e) => handlePriceChange(1, Math.max(parseInt(e?.target?.value), priceRange?.[0] + 1))}
                  className="oh-range-max absolute w-full h-6 appearance-none bg-transparent"
                  style={{ zIndex: priceRange?.[0] > 100 ? 3 : 5 }}
                  aria-label="Maximum price"
                />
              </div>
            </div>
          </FilterSection>

          {/* Categories — nested tree (parent + indented children) */}
          <FilterSection title="Categories">
            <div className="space-y-3">
              {categories?.map((category) => (
                <div key={category?.id}>
                  <div className="flex items-center justify-between">
                    <Checkbox
                      label={category?.label}
                      checked={filters?.categories?.includes(category?.id) || false}
                      onChange={(e) => handleCheckboxChange('categories', category?.id, e?.target?.checked)}
                    />
                    <span className="text-xs text-muted-foreground">({category?.count})</span>
                  </div>
                  {category?.children?.length > 0 && (
                    <div className="ml-5 mt-2 space-y-2 border-l border-border pl-3">
                      {category.children.map((child) => (
                        <div key={child?.id} className="flex items-center justify-between">
                          <Checkbox
                            label={child?.label}
                            checked={filters?.categories?.includes(child?.id) || false}
                            onChange={(e) => handleCheckboxChange('categories', child?.id, e?.target?.checked)}
                          />
                          <span className="text-xs text-muted-foreground">({child?.count})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </FilterSection>

          {/* Collections — curated selections that live as WC categories under the
              hood (ids 60/61/62). Clicks bind into filters.categories so the same
              server-side fetch handles them; getCategories() in wp-api filters them
              out of the main Categories tree to avoid duplication. */}
          <FilterSection title="Collections">
            <div className="space-y-3">
              {collections?.map((c) => (
                <div key={c?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={c?.label}
                    checked={filters?.categories?.includes(c?.id) || false}
                    onChange={(e) => handleCheckboxChange('categories', c?.id, e?.target?.checked)}
                  />
                </div>
              ))}
            </div>
          </FilterSection>

          {/* Ratings */}
          <FilterSection title="Customer Rating">
            <div className="space-y-3">
              {ratings?.map((rating) => (
                <div key={rating?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={rating?.label}
                    checked={filters?.ratings?.includes(rating?.id) || false}
                    onChange={(e) => handleCheckboxChange('ratings', rating?.id, e?.target?.checked)}
                  />
                  <span className="text-xs text-muted-foreground">({rating?.count})</span>
                </div>
              ))}
            </div>
          </FilterSection>

          {/* Size */}
          <FilterSection title="Size">
            <div className="space-y-3">
              {sizes?.map((type) => (
                <div key={type?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={type?.label}
                    checked={filters?.sizes?.includes(type?.id) || false}
                    onChange={(e) => handleCheckboxChange('sizes', type?.id, e?.target?.checked)}
                  />
                  <span className="text-xs text-muted-foreground">({type?.count})</span>
                </div>
              ))}
            </div>
          </FilterSection>

          {/* Apply Filters Button (Mobile) */}
          <div className="lg:hidden mt-6">
            <Button fullWidth onClick={onClose}>
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;